import { formatJobNumber } from '@fieldops/shared'
import { useCallback, useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react'
import { formatTimeRange } from '../../../lib/format.js'
import {
  MINUTES_VISIBLE,
  SLOT_MINUTES,
  addMinutes,
  durationLabel,
  kilometresBetween,
  minutesBetween,
  moveTo,
  overlaps,
  resizeTo,
  snapMinutes,
  spanTo,
  type Coordinates,
} from './geometry.js'
import { jobHeadline } from './labels.js'
import {
  bucketIdOf,
  resourceOfVisit,
  userIdOf,
  userResource,
  type ResourceId,
} from './resource.js'
import type { BoardVisit, Placement, UnscheduledJob } from './types.js'

/**
 * Direct manipulation on the board.
 *
 * Written by hand rather than on dnd-kit, and the reasons are specific:
 *
 * - **The grab offset has to survive.** dnd-kit hands back a delta from the
 *   activator event; the previous board added that to the pointer and snapped
 *   the block's *top* there, so grabbing a two-hour job by its middle threw it
 *   an hour early. Here the offset from the block's top edge is measured at
 *   pointer-down and subtracted for the rest of the gesture.
 * - **Nothing may be computed from a stale rect.** The destination is worked
 *   out from live pointer coordinates against the column's rectangle read at
 *   that moment, which is what makes edge auto-scrolling possible at all: the
 *   grid can move under the pointer mid-drag and the answer stays right.
 * - **Resize is the same machine as move.** One edge follows the pointer, the
 *   other is pinned, and a visit can never be shorter than one slot.
 * - **A drag on empty grid draws a slot.** Same snapping as a resize, both
 *   edges live, then Book a job or Add event. Shift/cmd still marquees.
 *
 * The preview is state, so it renders. The gesture itself is a ref, because a
 * pointermove at 120Hz should not be a React render if nothing visible has
 * changed.
 */

const DRAG_SLOP_PX = 4
const AUTOSCROLL_EDGE_PX = 56
const AUTOSCROLL_MAX_PX = 18

type Axis = 'y' | 'x'

interface MoveGesture {
  kind: 'move'
  /** The block under the pointer. The rest of the selection follows it. */
  primaryId: string
  visits: BoardVisit[]
  grabOffsetPx: number
  originResourceId: ResourceId
  /** Shift-drag on a crew: this person peels off. Decision 69. */
  detachUserId?: string
}

interface ResizeGesture {
  kind: 'resize'
  visit: BoardVisit
  edge: 'start' | 'end'
  resourceId: ResourceId
}

interface CreateGesture {
  kind: 'create'
  job: UnscheduledJob
  durationMinutes: number
  grabOffsetPx: number
}

interface SpanGesture {
  kind: 'span'
  resourceId: ResourceId
  originOffsetPx: number
}

type Gesture = (MoveGesture | ResizeGesture | CreateGesture | SpanGesture) & {
  pointerId: number
  startClientX: number
  startClientY: number
  armed: boolean
}

export interface GesturePreview {
  /** Where each moving visit would land. */
  placements: Map<string, Placement>
  /** Where a job dragged off the rail would land. */
  create: (Placement & { label: string }) | null
  /** Loaded visits the preview would clash with. */
  conflicts: Set<string>
  /** "08:15–09:45 · 1h 30m", shown against the block being manipulated. */
  label: string | null
  /**
   * Straight-line kilometres from whatever is being dragged to every other
   * booking on the board that has coordinates. Empty outside a move or
   * create gesture, or when the dragged job's own site never geocoded.
   */
  distanceKm: Map<string, number>
  /** Person being peeled off a shared booking, or null. */
  detachUserId: string | null
  /** Whose copy is under the pointer. Teammates stay put. */
  originUserId: string | null
}

const EMPTY_PREVIEW: GesturePreview = {
  placements: new Map(),
  create: null,
  conflicts: new Set(),
  label: null,
  distanceKm: new Map(),
  detachUserId: null,
  originUserId: null,
}

/** The one thing every haversine call needs, read off a job however it arrives. */
function jobCoordinates(job: {
  site?: { address?: { latitude: number | null; longitude: number | null } | null } | null
}): Coordinates | null {
  const address = job.site?.address
  if (!address || address.latitude == null || address.longitude == null) return null
  return { latitude: address.latitude, longitude: address.longitude }
}

/** Distance from `origin` to every visit in `candidates` whose site geocoded. */
function distancesFrom(origin: Coordinates, candidates: BoardVisit[]): Map<string, number> {
  const distances = new Map<string, number>()
  for (const visit of candidates) {
    const coordinates = jobCoordinates(visit.job)
    if (coordinates) distances.set(visit.id, kilometresBetween(origin, coordinates))
  }
  return distances
}

export interface BoardGestures {
  registerResource: (resourceId: ResourceId, element: HTMLElement | null) => void
  beginMove: (
    event: ReactPointerEvent,
    visit: BoardVisit,
    selection: BoardVisit[],
    options?: { originUserId?: string; detachUserId?: string },
  ) => void
  beginResize: (event: ReactPointerEvent, visit: BoardVisit, edge: 'start' | 'end') => void
  beginCreate: (event: ReactPointerEvent, job: UnscheduledJob) => void
  /** Pointer-down on empty grid. Draws a start–end slot on that row. */
  beginSpan: (event: ReactPointerEvent, resourceId: ResourceId) => void
  /** Same maths as a drag, for the keyboard. Returns null if nothing moved. */
  nudge: (visits: BoardVisit[], minutes: number) => Array<Placement & { id: string }> | null
  stretch: (visit: BoardVisit, minutes: number) => (Placement & { id: string }) | null
  preview: GesturePreview
  /** True once the pointer has travelled far enough to be a drag, not a tap. */
  isDragging: boolean
  activeIds: Set<string>
}

export function useBoardGestures({
  windowStart,
  axis,
  slotPx,
  visits,
  scrollerRef,
  onMove,
  onSplit,
  onCreate,
  onPark,
  onParkVisits,
  canPark,
  onTap,
  onSpan,
  onEmptyTap,
  enabled = true,
}: {
  windowStart: Date
  axis: Axis
  slotPx: number
  visits: BoardVisit[]
  scrollerRef: React.RefObject<HTMLElement | null>
  onMove: (moves: Array<{ id: string; startAt: Date; endAt: Date; userIds?: string[] }>) => void
  onSplit?: (input: {
    id: string
    userId: string
    startAt: Date
    endAt: Date
    landOnUserId?: string
  }) => void
  onCreate: (job: UnscheduledJob, placement: Placement) => void
  onPark: (job: UnscheduledJob, bucketId: string) => void
  /** Bookings dragged into a bucket: each visit goes and its job is filed. */
  onParkVisits?: (entries: Array<{ visitId: string; bucketId: string }>) => void
  /**
   * Whether this block is a booking whose job can be filed. A break or a
   * meeting is not — a `CalendarEvent` has no job to put in a bucket
   * (decision 67) — so for those a bucket is not a drop target at all.
   */
  canPark?: (visitId: string) => boolean
  /** A click that never became a drag. Opens the quick menu, not the job. */
  onTap?: (visitId: string, point: { x: number; y: number }) => void
  /** A drag on empty grid that armed. The times are already snapped. */
  onSpan?: (placement: Placement & { label: string }, point: { x: number; y: number }) => void
  /** A click on empty grid that never became a drag. Clears the selection. */
  onEmptyTap?: () => void
  enabled?: boolean
}): BoardGestures {
  const rows = useRef(new Map<ResourceId, HTMLElement>())
  const gesture = useRef<Gesture | null>(null)
  const pointer = useRef({ x: 0, y: 0 })
  const autoscroll = useRef<number | null>(null)

  const [preview, setPreview] = useState<GesturePreview>(EMPTY_PREVIEW)
  const [isDragging, setIsDragging] = useState(false)

  const registerResource = useCallback((resourceId: ResourceId, element: HTMLElement | null) => {
    if (element) rows.current.set(resourceId, element)
    else rows.current.delete(resourceId)
  }, [])

  /**
   * Which row the pointer is over, and how far along its time axis.
   *
   * Read fresh every frame. The rectangles are the source of truth, so a grid
   * that scrolls mid-drag does not shift the answer.
   */
  const locate = useCallback(
    (clientX: number, clientY: number): { resourceId: ResourceId; offsetPx: number } | null => {
      let fallback: { resourceId: ResourceId; offsetPx: number; distance: number } | null = null

      for (const [resourceId, element] of rows.current) {
        const rect = element.getBoundingClientRect()
        const along = axis === 'y' ? clientY - rect.top : clientX - rect.left
        const across = axis === 'y' ? clientX : clientY
        const low = axis === 'y' ? rect.left : rect.top
        const high = axis === 'y' ? rect.right : rect.bottom

        if (across >= low && across <= high) return { resourceId, offsetPx: along }

        // Dragging past the last column should not drop the gesture; the
        // nearest column is what the dispatcher was aiming at.
        const distance = across < low ? low - across : across - high
        if (!fallback || distance < fallback.distance) {
          fallback = { resourceId, offsetPx: along, distance }
        }
      }

      return fallback ? { resourceId: fallback.resourceId, offsetPx: fallback.offsetPx } : null
    },
    [axis],
  )

  /** Visits the preview would sit on top of, ignoring the ones being moved. */
  const findConflicts = useCallback(
    (placements: Map<string, Placement>, extra: Placement | null): Set<string> => {
      const moving = new Set(placements.keys())
      const proposals = [...placements.values(), ...(extra ? [extra] : [])]
      const clashing = new Set<string>()

      for (const visit of visits) {
        if (moving.has(visit.id)) continue
        if (visit.status === 'CANCELLED' || visit.status === 'NO_SHOW') continue

        // Only a person can be double-booked. Two jobs at the same time on the
        // unassigned row are not a clash — nobody is committed to either yet.
        const assigned = new Set(
          visit.assignments.map((assignment) => userResource(assignment.userId)),
        )

        for (const proposal of proposals) {
          if (!assigned.has(proposal.resourceId)) continue
          if (overlaps(visit, proposal)) {
            clashing.add(visit.id)
            break
          }
        }
      }

      return clashing
    },
    [visits],
  )

  const computePreview = useCallback((): GesturePreview => {
    const active = gesture.current
    if (!active || !active.armed) return EMPTY_PREVIEW

    if (active.kind === 'span') {
      const origin = rows.current.get(active.resourceId)
      if (!origin) return EMPTY_PREVIEW

      const rect = origin.getBoundingClientRect()
      const offsetPx = axis === 'y' ? pointer.current.y - rect.top : pointer.current.x - rect.left
      const { startAt, endAt } = spanTo(windowStart, active.originOffsetPx, offsetPx, slotPx)
      const placement: Placement = { resourceId: active.resourceId, startAt, endAt }
      const label = spanLabel(startAt, endAt)

      return {
        placements: new Map(),
        create: { ...placement, label },
        conflicts: findConflicts(new Map(), placement),
        label,
        distanceKm: new Map(),
        detachUserId: null,
        originUserId: null,
      }
    }

    const here = locate(pointer.current.x, pointer.current.y)
    if (!here) return EMPTY_PREVIEW

    if (active.kind === 'create') {
      const { startAt, endAt } = moveTo(
        windowStart,
        here.offsetPx,
        active.grabOffsetPx,
        active.durationMinutes,
        slotPx,
      )
      const placement: Placement = { resourceId: here.resourceId, startAt, endAt }
      const origin = jobCoordinates(active.job)

      return {
        placements: new Map(),
        create: {
          ...placement,
          label: `${formatJobNumber(active.job)} ${jobHeadline(active.job)}`,
        },
        conflicts: findConflicts(new Map(), placement),
        label: spanLabel(startAt, endAt),
        distanceKm: origin ? distancesFrom(origin, visits) : new Map(),
        detachUserId: null,
        originUserId: null,
      }
    }

    if (active.kind === 'resize') {
      const fixed = active.edge === 'start' ? active.visit.endAt : active.visit.startAt
      const { startAt, endAt } = resizeTo(windowStart, active.edge, here.offsetPx, fixed, slotPx)
      const placements = new Map<string, Placement>([
        [active.visit.id, { resourceId: active.resourceId, startAt, endAt }],
      ])

      return {
        placements,
        create: null,
        conflicts: findConflicts(placements, null),
        label: spanLabel(startAt, endAt),
        distanceKm: new Map(),
        detachUserId: null,
        originUserId: null,
      }
    }

    if (active.kind !== 'move') return EMPTY_PREVIEW

    const primary = active.visits.find((visit) => visit.id === active.primaryId)
    if (!primary) return EMPTY_PREVIEW

    const primaryDuration = Math.max(SLOT_MINUTES, minutesBetween(primary.startAt, primary.endAt))
    const moved = moveTo(windowStart, here.offsetPx, active.grabOffsetPx, primaryDuration, slotPx)
    const shift = minutesBetween(primary.startAt, moved.startAt)

    // A bucket is somewhere to file a *job*, so it is a target only for a plain
    // drag of a real booking. A break has no job to file, and a peeled crew
    // member is a person rather than a job — for both, the pointer over a
    // bucket reads as still being on the row the block came from, so nothing
    // lights up and nothing is refused after the fact.
    const filing =
      !active.detachUserId && (canPark?.(active.primaryId) ?? true)
    const landing =
      bucketIdOf(here.resourceId) && !filing ? active.originResourceId : here.resourceId
    const rowChanged = landing !== active.originResourceId

    const placements = new Map<string, Placement>()
    for (const visit of active.visits) {
      const startAt = visit.id === active.primaryId ? moved.startAt : addMinutes(visit.startAt, shift)
      const duration = Math.max(SLOT_MINUTES, minutesBetween(visit.startAt, visit.endAt))

      placements.set(visit.id, {
        // A selection dragged as one shifts by the same amount and, if the
        // gesture crossed rows, lands on the same one. One rule, so there is
        // nothing to learn about which blocks followed and which did not.
        resourceId: rowChanged
          ? landing
          : visit.id === active.primaryId
            ? active.originResourceId
            : resourceOfVisit(visit),
        startAt,
        endAt: addMinutes(startAt, duration),
      })
    }

    const origin = jobCoordinates(primary.job)
    const travelling = new Set(active.visits.map((visit) => visit.id))

    return {
      placements,
      create: null,
      conflicts: findConflicts(placements, null),
      label: spanLabel(moved.startAt, moved.endAt),
      distanceKm: origin
        ? distancesFrom(
            origin,
            visits.filter((visit) => !travelling.has(visit.id)),
          )
        : new Map(),
      detachUserId: active.detachUserId ?? null,
      originUserId: userIdOf(active.originResourceId),
    }
  }, [axis, canPark, findConflicts, locate, slotPx, visits, windowStart])

  /**
   * Scrolls when the pointer nears an edge.
   *
   * The preview is recomputed on every frame of the scroll, not just on
   * pointermove, because the grid is what is moving.
   */
  const step = useCallback(() => {
    autoscroll.current = null
    const active = gesture.current
    if (!active) return

    const scroller = scrollerRef.current
    if (scroller) {
      const rect = scroller.getBoundingClientRect()
      const dy = edgeVelocity(pointer.current.y, rect.top, rect.bottom)
      const dx = edgeVelocity(pointer.current.x, rect.left, rect.right)
      if (dy || dx) scroller.scrollBy({ top: dy, left: dx })
    }

    setPreview(computePreview())
    autoscroll.current = requestAnimationFrame(step)
  }, [computePreview, scrollerRef])

  const finish = useCallback(
    (commit: boolean) => {
      const active = gesture.current
      const tap =
        commit && active && !active.armed && active.kind === 'move'
          ? { id: active.primaryId, x: pointer.current.x, y: pointer.current.y }
          : null
      const emptyTap = commit && active && !active.armed && active.kind === 'span'

      // Read before the gesture is cleared, because the preview is derived
      // from it — clearing first leaves every drop with nothing to commit.
      const settled = active?.armed && commit ? computePreview() : null
      gesture.current = null

      if (autoscroll.current !== null) {
        cancelAnimationFrame(autoscroll.current)
        autoscroll.current = null
      }

      setIsDragging(false)
      setPreview(EMPTY_PREVIEW)
      document.body.classList.remove('board-dragging')

      if (tap) {
        onTap?.(tap.id, { x: tap.x, y: tap.y })
        return
      }

      if (emptyTap) {
        onEmptyTap?.()
        return
      }

      if (!active || !settled) return

      if (active.kind === 'span') {
        if (!settled.create) return
        onSpan?.(settled.create, { x: pointer.current.x, y: pointer.current.y })
        return
      }

      if (active.kind === 'create') {
        if (!settled.create) return

        const { label: _label, ...placement } = settled.create
        const bucketId = bucketIdOf(placement.resourceId)

        // Dropped on a bucket rather than on the grid: this is filing, not
        // booking, so no visit is created and the job keeps its lack of a date.
        if (bucketId) onPark(active.job, bucketId)
        else onCreate(active.job, placement)
        return
      }

      if (active.kind === 'move' && active.detachUserId) {
        const placement = settled.placements.get(active.primaryId)
        // A bucket holds jobs, and peeling somebody off a crew produces a
        // person. `computePreview` already keeps a detaching drag off a bucket
        // row; this is the same rule where the write happens.
        if (!placement || bucketIdOf(placement.resourceId)) return
        const landedOn = userIdOf(placement.resourceId)
        onSplit?.({
          id: active.primaryId,
          userId: active.detachUserId,
          startAt: placement.startAt,
          endAt: placement.endAt,
          ...(landedOn ? { landOnUserId: landedOn } : {}),
        })
        return
      }

      const settledPlacements = [...settled.placements.entries()]

      // Dropped in a bucket: the job is being taken off the board and filed,
      // so the visit goes with it. Decision 45 refused this drag because it
      // destroys a time; decision 76 answered that by refusing to pick up a
      // confirmed booking at all, so what lands here is only ever a slot
      // nobody agreed to. One call for the whole selection.
      const parked = settledPlacements.flatMap(([visitId, placement]) => {
        const bucketId = bucketIdOf(placement.resourceId)
        return bucketId ? [{ visitId, bucketId }] : []
      })

      if (parked.length > 0) onParkVisits?.(parked)

      const moves = settledPlacements
        .filter(([, placement]) => !bucketIdOf(placement.resourceId))
        .map(([id, placement]) => {
          const before = visits.find((visit) => visit.id === id)
          const rowChanged = before ? resourceOfVisit(before) !== placement.resourceId : true
          const landedOn = userIdOf(placement.resourceId)

          return {
            id,
            startAt: placement.startAt,
            endAt: placement.endAt,
            // Omitted when the row did not change, because sending it rewrites
            // the assignment rows and would drop a second assignee. Dropping
            // on the unassigned row sends an empty list, which is how a
            // booking is taken off a person without losing its time.
            ...(rowChanged && (before?.assignments.length ?? 0) < 2
              ? { userIds: landedOn ? [landedOn] : [] }
              : {}),
          }
        })
        .filter((entry) => {
          const before = visits.find((visit) => visit.id === entry.id)
          if (!before) return false
          return (
            before.startAt.getTime() !== entry.startAt.getTime() ||
            before.endAt.getTime() !== entry.endAt.getTime() ||
            'userIds' in entry
          )
        })

      if (moves.length > 0) onMove(moves)
    },
    [
      computePreview,
      onCreate,
      onMove,
      onPark,
      onParkVisits,
      onEmptyTap,
      onSpan,
      onSplit,
      onTap,
      visits,
    ],
  )

  // One set of window listeners for the life of the board rather than a set per
  // gesture, so a pointerup that lands outside the grid still ends the drag.
  useEffect(() => {
    const onPointerMove = (event: PointerEvent) => {
      const active = gesture.current
      if (!active || event.pointerId !== active.pointerId) return

      pointer.current = { x: event.clientX, y: event.clientY }

      if (!active.armed) {
        const travelled = Math.hypot(
          event.clientX - active.startClientX,
          event.clientY - active.startClientY,
        )
        // Below the slop this is still a tap, and a tap opens the quick menu.
        if (travelled < DRAG_SLOP_PX) return

        active.armed = true
        setIsDragging(true)
        document.body.classList.add('board-dragging')
        if (autoscroll.current === null) autoscroll.current = requestAnimationFrame(step)
      }

      event.preventDefault()
      setPreview(computePreview())
    }

    const onPointerUp = (event: PointerEvent) => {
      if (!gesture.current || event.pointerId !== gesture.current.pointerId) return
      finish(true)
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && gesture.current) finish(false)
    }

    window.addEventListener('pointermove', onPointerMove, { passive: false })
    window.addEventListener('pointerup', onPointerUp)
    window.addEventListener('pointercancel', onPointerUp)
    window.addEventListener('keydown', onKeyDown)

    return () => {
      window.removeEventListener('pointermove', onPointerMove)
      window.removeEventListener('pointerup', onPointerUp)
      window.removeEventListener('pointercancel', onPointerUp)
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [computePreview, finish, step])

  useEffect(
    () => () => {
      if (autoscroll.current !== null) cancelAnimationFrame(autoscroll.current)
      document.body.classList.remove('board-dragging')
    },
    [],
  )

  const begin = useCallback(
    (event: ReactPointerEvent, partial: MoveGesture | ResizeGesture | CreateGesture | SpanGesture) => {
      if (!enabled || event.button !== 0) return

      pointer.current = { x: event.clientX, y: event.clientY }
      gesture.current = {
        ...partial,
        pointerId: event.pointerId,
        startClientX: event.clientX,
        startClientY: event.clientY,
        armed: false,
      }
    },
    [enabled],
  )

  const beginMove = useCallback(
    (
      event: ReactPointerEvent,
      visit: BoardVisit,
      selection: BoardVisit[],
      options?: { originUserId?: string; detachUserId?: string },
    ) => {
      const block = event.currentTarget.getBoundingClientRect()

      begin(event, {
        kind: 'move',
        primaryId: visit.id,
        visits: selection.some((entry) => entry.id === visit.id) ? selection : [visit],
        // The whole point: where in the block the pointer took hold.
        grabOffsetPx: axis === 'y' ? event.clientY - block.top : event.clientX - block.left,
        originResourceId: options?.originUserId
          ? userResource(options.originUserId)
          : resourceOfVisit(visit),
        detachUserId: options?.detachUserId,
      })
    },
    [axis, begin],
  )

  const beginResize = useCallback(
    (event: ReactPointerEvent, visit: BoardVisit, edge: 'start' | 'end') => {
      event.stopPropagation()
      begin(event, { kind: 'resize', visit, edge, resourceId: resourceOfVisit(visit) })
    },
    [begin],
  )

  const beginCreate = useCallback(
    (event: ReactPointerEvent, job: UnscheduledJob) => {
      begin(event, {
        kind: 'create',
        job,
        // An hour when nobody estimated: long enough to see on the grid, short
        // enough to be obviously a placeholder.
        durationMinutes: job.estimatedMinutes ?? 60,
        // A card off the rail is grabbed at its own top edge, so the block
        // lands under the pointer rather than centred on it.
        grabOffsetPx: 0,
      })
    },
    [begin],
  )

  const beginSpan = useCallback(
    (event: ReactPointerEvent, resourceId: ResourceId) => {
      if (bucketIdOf(resourceId)) return

      const element = rows.current.get(resourceId) ?? (event.currentTarget as HTMLElement)
      const rect = element.getBoundingClientRect()
      const originOffsetPx = axis === 'y' ? event.clientY - rect.top : event.clientX - rect.left

      begin(event, { kind: 'span', resourceId, originOffsetPx })
    },
    [axis, begin],
  )

  /** Arrow keys. The same clamping as a drag, without a pointer. */
  const nudge = useCallback(
    (selection: BoardVisit[], minutes: number) => {
      if (selection.length === 0) return null

      const shifted = selection.map((visit) => {
        const duration = Math.max(SLOT_MINUTES, minutesBetween(visit.startAt, visit.endAt))
        const startMinutes = minutesBetween(windowStart, visit.startAt) + minutes
        const clamped = Math.max(0, Math.min(startMinutes, MINUTES_VISIBLE - duration))
        const startAt = addMinutes(windowStart, clamped)

        return {
          id: visit.id,
          resourceId: resourceOfVisit(visit),
          startAt,
          endAt: addMinutes(startAt, duration),
        }
      })

      const anyMoved = shifted.some((entry, index) => {
        const before = selection[index]!
        return entry.startAt.getTime() !== before.startAt.getTime()
      })

      return anyMoved ? shifted : null
    },
    [windowStart],
  )

  /** `[` and `]`. Grows or shrinks from the end, never below one slot. */
  const stretch = useCallback(
    (visit: BoardVisit, minutes: number) => {
      const endMinutes = minutesBetween(windowStart, visit.endAt) + minutes
      const { startAt, endAt } = resizeTo(
        windowStart,
        'end',
        (Math.max(0, endMinutes) / SLOT_MINUTES) * slotPx,
        visit.startAt,
        slotPx,
      )

      if (endAt.getTime() === visit.endAt.getTime()) return null

      return { id: visit.id, resourceId: resourceOfVisit(visit), startAt, endAt }
    },
    [slotPx, windowStart],
  )

  const activeIds = useMemo(() => new Set(preview.placements.keys()), [preview])

  return {
    registerResource,
    beginMove,
    beginResize,
    beginCreate,
    beginSpan,
    nudge,
    stretch,
    preview,
    isDragging,
    activeIds,
  }
}

function spanLabel(startAt: Date, endAt: Date): string {
  return `${formatTimeRange(startAt, endAt)} · ${durationLabel(minutesBetween(startAt, endAt))}`
}

/** Faster the closer the pointer is to the edge, zero once clear of it. */
function edgeVelocity(position: number, low: number, high: number): number {
  if (position < low + AUTOSCROLL_EDGE_PX) {
    const depth = (low + AUTOSCROLL_EDGE_PX - position) / AUTOSCROLL_EDGE_PX
    return -Math.round(Math.min(1, depth) * AUTOSCROLL_MAX_PX)
  }

  if (position > high - AUTOSCROLL_EDGE_PX) {
    const depth = (position - (high - AUTOSCROLL_EDGE_PX)) / AUTOSCROLL_EDGE_PX
    return Math.round(Math.min(1, depth) * AUTOSCROLL_MAX_PX)
  }

  return 0
}

/** Exported for the tests, which pin the snapping the whole board depends on. */
export { snapMinutes }
